import React, { useState, useRef } from 'react';
import { dayjs, formatLocalTime, toLocalTime } from '../../../components/ex_dayjs';
import { DatagridConfigurable, FunctionField, TextField, Title, useDataProvider } from 'react-admin';
import RenderControls from '../../../components/ra-list/RenderControls';
import RaList from '../../../components/ra-list/RaList';
import { convertDataForMSelect, formatEnumToName, getRaStore, setDefaultValuesFromURL, setRaStore } from '../../../utils/common';
import ChartButton from './components/chart-button';
import ChartView from './components/chart-view';
import { FilterListProvider } from '../../../context/FilterListProvider';

const DifficultEventTrackingReport = () => {
    const dataProvider = useDataProvider();
    const refbtnFilter = useRef(null);
    const [isLoadingCategory, setIsLoadingCategory] = useState(false);
    const [allTypes, setAllTypes] = useState(null);
    const [defaultValueFilter, setDefaultValuesFilter] = useState({
        utc_Posted_On: [dayjs().subtract(2, 'weeks'), dayjs()]
    });
    const [chart, setChart] = useState({ open: false, filterList: {} });

    /**
     * Handle chart view button click - opens chart modal
     */
    const handleShowChart = (filterList: any) => setChart({ open: true, filterList });

    /**
     * Custom search handler to bypass useListContext requirement
     */
    const handleCustomSearch = (filters: any) => handleShowChart(filters);

    const controls = [
        	{ component: 'select', name: 'type_eq', label: 'Market', choices: allTypes?.market || [] },
        { component: 'daterange', name: 'utc_Posted_On', label: 'Date', xs: 3, isUTC: true },
        { component: 'text', name: 'event', label: 'Event', xs: 3 },
        { component: 'text', name: 'venue', label: 'Venue', xs: 3 },
        { component: 'select', name: 'Category_eq', label: 'Category', xs: 2, choices: [{ id: 'ProcessTracking', name: 'Processed Orders' }, { id: 'UnprocessedOrders', name: 'Unprocessed Orders' }] },
        { component: 'select', name: 'Card_Brand_eq', label: 'Card Brand', choices: allTypes?.cardBrands || [] },
        { component: 'select', name: 'Ip_Type_eq', label: 'IP Type', xs: 1.5, choices: allTypes?.ipTypes || [] },
        { component: 'select', name: 'Browser_eq', label: 'Browser',  xs: 1.5, choices: allTypes?.browserTypes || [] },
        { component: 'select', name: 'Account_Type_eq', label: 'Account Type',  xs: 1.5, choices: allTypes?.accountTypes || [] },
    
    ];

    React.useEffect(() => {
        let _orderProcessTypes = getRaStore('difficultEventTrackingTypes');
        if (!_orderProcessTypes) {
            setIsLoadingCategory(true);
            dataProvider.fetchData('DifficultEventTrackingReport', 'GetAllTypes').then((response: any) => {
                if (response.status !== 200 && response.data) {
                    let cardBrands = convertDataForMSelect(response.data.cardBrands, false);
                    let accountTypes = convertDataForMSelect(response.data.accountTypes);
                    let ipTypes = convertDataForMSelect(response.data.ipTypes, false);
                    let browserTypes = convertDataForMSelect(response.data.browserTypes, false);
                    let market = convertDataForMSelect(response.data.market, false);
                    setAllTypes({ market, ipTypes, accountTypes, browserTypes, cardBrands });
                    setRaStore('difficultEventTrackingTypes', { market, ipTypes, accountTypes, browserTypes, cardBrands });

                    setIsLoadingCategory(false);
                }
            }).catch((error: any) => console.log(error));
        } else {
            setAllTypes(_orderProcessTypes);
        }

        if (location.search) {
            let newDefaultValue = { ...defaultValueFilter };
            setDefaultValuesFromURL(location.search, newDefaultValue, controls);
            setDefaultValuesFilter(newDefaultValue);
        }
    }, []);

    

    return (
        <>
            <Title title="Difficult Event Tracking Report" />
            {isLoadingCategory && <p>Loading category ...</p>}
            
            
            <div style={{
                padding: '16px',
                borderRadius: '4px',
                marginBottom: '20px'
            }}>
                <RenderControls
                    controls={controls}
                    refFilter={refbtnFilter}
                    defaultValues={defaultValueFilter}
                    useHashUrl
                    selectColumn={false}
                    actions={<></>}
                    onSearch={handleCustomSearch}
                    standalone={true}
                />
            </div>
            
            
            <ChartView chart={chart} setChart={setChart} />
            
            
            {false && (
                <RaList
                    sortby={['utc_Posted_On desc']}
                    perPage={25}
                    title={'Difficult Event Tracking Report'}
                    filters={
                        <FilterListProvider>
                            <RenderControls
                                controls={controls}
                                refFilter={refbtnFilter}
                                defaultValues={defaultValueFilter}
                                useHashUrl
                                selectColumn={true}
                                actions={<ChartButton callback={handleShowChart} />}
                                exportFile={{
                                    filename: `DifficultEventTrackingReport-${formatLocalTime(new Date(), 'YY-MM-DD')}`,
                                    columns: ['Type', 'Event', 'Venue', 'Ip_Type_Name', 'Browser_Name', 'Card_Brand_Name',
                                        'Account_Type_Name', 'Utc_Posted_On'],
                                    limit: 1000000
                                }}
                            />
                        </FilterListProvider>
                    }
                >
                    <DatagridConfigurable omit={['order_Id', 'ip', 'incognito_Browser', 'other_State', 'same_Billing_Venue', 'author', 'order_Number']}
                        bulkActionButtons={false}
                    >
                        <TextField source="type" label={'Market'} />
                        <TextField source="event" label={'Event'} />
                        <TextField source="venue" label={'Venue'} />
                        <TextField source="browser_Name" label={'Browser'} />
                        <TextField source="ip_Type_Name" label={'IP Type'} />
                        <TextField source="card_Brand_Name" label={'Card Brand'} />
                        <FunctionField source="account_Type_Name" label={'Account Type'} render={record => formatEnumToName(record.account_Type_Name)} />
                        <TextField source="author" label={'Author'} />
                        <FunctionField label='Date' source='utc_Posted_On' render={record => (
                            <><b>{toLocalTime(record.utc_Posted_On).format('MM/DD/YYYY')}</b>
                                <br />{toLocalTime(record.utc_Posted_On).format('hh:mm A')}</>)}
                        />
                        <FunctionField label='OrderNumber' source='order_Number' render={record => (
                            record.category === 'UnprocessedOrders' ? record.order_Number : ''
                        )} />
                    </DatagridConfigurable>
                </RaList >
            )}
        </>
    );
};

const DifficultEventTrackingReportPage = {
    name: 'DifficultEventTrackingReport',
    list: DifficultEventTrackingReport
};
export default DifficultEventTrackingReportPage;
